import {
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  LoaderIcon,
  SquareArrowOutDownRightIcon,
} from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  getRawCustomReport,
  getRawReportSection,
} from "~/api/custom-reports/custom-reports";
import type { ReportSectionMeta } from "~/api/model";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  EMAIL_EXPORT_MAX_CHARACTERS,
  EXPORT_LIST,
  EXPORT_TYPE,
} from "~/features/chat/constants";
import {
  downloadStringAsFile,
  downloadSummaryAsDocx,
  downloadSummaryAsPdf,
  sendSummaryWithEmail,
} from "~/features/chat/helpers";
import { useChatDocumentParams } from "~/helpers";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";
import { normalizeLiteralNewlines } from "~/utils/markdownTerms";

type ReportCopyExportActionsProps = {
  content: string;
  reportId?: string;
  reportSections?: ReportSectionMeta[];
  fileName: string;
  title?: string;
  disabled?: boolean;
  buttonSize?: "sm" | "mini";
  copyEventName?: string;
  exportOpenEventName?: string;
  exportEventName?: string;
  caseInfo?: {
    caseName: string;
    dateOfBirth: string;
    dateOfLoss: string;
    claimNumber: string;
  };
};

export const ReportCopyExportActions: FC<ReportCopyExportActionsProps> = ({
  content,
  reportId,
  reportSections,
  fileName,
  title,
  disabled,
  buttonSize = "sm",
  copyEventName,
  exportOpenEventName,
  exportEventName,
  caseInfo,
}) => {
  const { organizationId } = useChatDocumentParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { isCopied, handleCopy } = useCopyContent();
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const fetchRawContent = useCallback(async (): Promise<string> => {
    if (!reportId) return content;

    const completedSections = reportSections?.filter(
      (s) => s.status === "completed"
    );

    if (completedSections && completedSections.length > 0) {
      const rawSections = await Promise.all(
        completedSections.map((s) => getRawReportSection(organizationId, s.id))
      );
      return rawSections
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(
          (section) =>
            `## ${section.title}\n\n${normalizeLiteralNewlines(section.rawContent ?? "")}`
        )
        .join("\n\n");
    }

    const rawReport = await getRawCustomReport(organizationId, reportId);
    if (rawReport.reportSections && rawReport.reportSections.length > 0) {
      return rawReport.reportSections
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(
          (section) =>
            `## ${section.title}\n\n${normalizeLiteralNewlines(section.rawContent ?? "")}`
        )
        .join("\n\n");
    }
    return normalizeLiteralNewlines(rawReport.text ?? "");
  }, [organizationId, reportId, reportSections, content]);

  const resolveAndAct = async (action: (resolved: string) => void) => {
    setIsLoadingContent(true);
    try {
      const resolved = await fetchRawContent();
      action(resolved);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleCopyClick = () => {
    if (copyEventName) {
      trackEvent({ eventName: copyEventName });
    }
    resolveAndAct((c) => handleCopy(c));
  };

  const handleExport = (type: EXPORT_TYPE) => {
    if (exportEventName) {
      trackEvent({ eventName: exportEventName, properties: { type } });
    }
    const exportTitle = title || "Summary";
    resolveAndAct((c) => {
      switch (type) {
        case EXPORT_TYPE.DOCX:
          downloadSummaryAsDocx(
            c,
            fileName,
            exportTitle,
            settings?.fontFamily,
            settings?.lineSpacing
          );
          break;
        case EXPORT_TYPE.PLAIN_TEXT:
          downloadStringAsFile(c, fileName);
          break;
        case EXPORT_TYPE.PDF:
          downloadSummaryAsPdf(
            c,
            fileName,
            exportTitle,
            caseInfo,
            settings?.fontFamily,
            settings?.lineSpacing,
            settings?.logoUrl,
            settings?.footnote
          );
          break;
        case EXPORT_TYPE.EMAIL:
          sendSummaryWithEmail(c, fileName);
          break;
      }
    });
  };

  const handleExportOpenChange = (newOpen: boolean) => {
    if (newOpen && disabled) return;
    setExportOpen(newOpen);
  };

  useEffect(() => {
    if (exportOpenEventName && exportOpen) {
      trackEvent({ eventName: exportOpenEventName });
    }
  }, [exportOpenEventName, exportOpen]);

  const availableExportOptions = useMemo(() => {
    const isEmailExportDisabled = content.length > EMAIL_EXPORT_MAX_CHARACTERS;
    return EXPORT_LIST.filter(
      (item) => !(item.type === EXPORT_TYPE.EMAIL && isEmailExportDisabled)
    );
  }, [content]);

  const isDisabled = disabled || isLoadingContent;

  return (
    <>
      <Button
        variant="ghost"
        size={buttonSize}
        onClick={handleCopyClick}
        disabled={isDisabled || isCopied}
        className={
          buttonSize === "mini"
            ? "text-foreground hover:text-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
            : undefined
        }
      >
        {isCopied ? (
          <>
            <CheckIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.copied" defaultMessage="Copied" />
          </>
        ) : isLoadingContent ? (
          <>
            <LoaderIcon className="w-4 h-4 mr-2 animate-spin" />
            <FormattedMessage id="common.copyAll" defaultMessage="Copy All" />
          </>
        ) : (
          <>
            <CopyIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.copyAll" defaultMessage="Copy All" />
          </>
        )}
      </Button>
      <DropdownMenu open={exportOpen} onOpenChange={handleExportOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size={buttonSize} disabled={isDisabled}>
            <SquareArrowOutDownRightIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.export" defaultMessage="Export" />
            <ChevronDownIcon className="w-4 h-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            {availableExportOptions.map((item) => (
              <DropdownMenuItem
                key={item.label}
                onClick={() => handleExport(item.type)}
              >
                <FormattedMessage id={item.label} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
